import { ApplicationConfig, provideZoneChangeDetection, LOCALE_ID, inject, isDevMode, provideAppInitializer } from "@angular/core";
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from "@angular/router";
import { provideHttpClient, withInterceptors, withJsonpSupport } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { provideServiceWorker } from '@angular/service-worker';

import { routes } from './app-routing.module';
import { authInterceptor } from './core/interceptors/auth/auth.interceptor';
import { AuthService } from './core/services/auth/auth.service';
import { PwaUpdateService } from './core/services/pwa/pwa-update.service';

import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideEnvironmentNgxCurrency, NgxCurrencyInputMode } from 'ngx-currency';

import { providePrimeNG } from "primeng/config";
import { FilterMatchMode, ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import Lara from "@primeuix/themes/lara";

registerLocaleData(localePt);

/**
 * Inicializa os dados do usuário após o app estar pronto
 * Evita dependência circular ao não fazer HTTP no construtor do AuthService
 */
function initializeAuth(): Promise<void> {
  // Aguarda a renovação da sessão (se o access token expirou com o app
  // fechado) antes de liberar o bootstrap: guards e header já nascem corretos.
  return inject(AuthService).initializeUserData();
}

const ptBrTranslations = {
  startsWith: 'Começa com',
  contains: 'Contém',
  notContains: 'Não contém',
  endsWith: 'Termina com',
  equals: 'Igual',
  notEquals: 'Diferente',
  noFilter: 'Sem filtro',
  lt: 'Menor que',
  lte: 'Menor que ou igual a',
  gt: 'Maior que',
  gte: 'Maior que ou igual a',
  is: 'É',
  isNot: 'Não é',
  before: 'Antes',
  after: 'Depois',
  dateIs: 'Data é',
  dateIsNot: 'Data não é',
  dateBefore: 'Data é antes',
  dateAfter: 'Data é depois',
  clear: 'Limpar',
  apply: 'Aplicar',
  matchAll: 'Corresponder a todos',
  matchAny: 'Corresponder a qualquer',
  addRule: 'Adicionar regra',
  removeRule: 'Remover regra',
  accept: 'Sim',
  reject: 'Não',
  choose: 'Escolher',
  upload: 'Enviar',
  cancel: 'Cancelar',
  completed: 'Concluído',
  pending: 'Pendente',
  dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
  dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sá'],
  monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
  monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
  today: 'Hoje',
  weekHeader: 'Sem',
  firstDayOfWeek: 0,
  dateFormat: 'dd/mm/yy',
  chooseYear: 'Escolher Ano',
  chooseMonth: 'Escolher Mês',
  chooseDate: 'Escolher Data',
  prevDecade: 'Década Anterior',
  nextDecade: 'Próxima Década',
  prevYear: 'Ano Anterior',
  nextYear: 'Próximo Ano',
  prevMonth: 'Mês Anterior',
  nextMonth: 'Próximo Mês',
  prevHour: 'Hora Anterior',
  nextHour: 'Próxima Hora',
  prevMinute: 'Minuto Anterior',
  nextMinute: 'Próximo Minuto',
  prevSecond: 'Segundo Anterior',
  nextSecond: 'Próximo Segundo',
  am: 'AM',
  pm: 'PM',
  weak: 'Fraco',
  medium: 'Médio',
  strong: 'Forte',
  passwordPrompt: 'Digite uma senha',
  emptyMessage: 'Nenhum resultado encontrado',
  emptyFilterMessage: 'Nenhum resultado encontrado',
  emptySearchMessage: 'Nenhum resultado encontrado',
  emptySelectionMessage: 'Nenhum item selecionado',
  searchMessage: '{0} resultados disponíveis',
  selectionMessage: '{0} itens selecionados',
  fileChosenMessage: 'Arquivos',
  noFileChosenMessage: 'Nenhum arquivo escolhido',
  aria: {
    trueLabel: 'Verdadeiro',
    falseLabel: 'Falso',
    nullLabel: 'Não Selecionado',
    star: '1 estrela',
    stars: '{star} estrelas',
    selectAll: 'Todos os itens selecionados',
    unselectAll: 'Todos os itens desmarcados',
    close: 'Fechar',
    previous: 'Anterior',
    next: 'Próximo',
    navigation: 'Navegação',
    scrollTop: 'Rolar para o topo',
    moveTop: 'Mover para o topo',
    moveUp: 'Mover para cima',
    moveDown: 'Mover para baixo',
    moveBottom: 'Mover para baixo',
    moveToTarget: 'Mover para o destino',
    moveToSource: 'Mover para a origem',
    moveAllToTarget: 'Mover todos para o destino',
    moveAllToSource: 'Mover todos para a origem',
    pageLabel: '{page}',
    firstPageLabel: 'Primeira Página',
    lastPageLabel: 'Última Página',
    nextPageLabel: 'Próxima Página',
    prevPageLabel: 'Página Anterior',
    previousPageLabel: 'Página Anterior',
    rowsPerPageLabel: 'Linhas por página',
    jumpToPageDropdownLabel: 'Ir para o dropdown de página',
    jumpToPageInputLabel: 'Ir para o input de página',
    selectRow: 'Linha Selecionada',
    unselectRow: 'Linha Desmarcada',
    expandRow: 'Linha Expandida',
    collapseRow: 'Linha Recolhida',
    showFilterMenu: 'Mostrar Menu de Filtro',
    hideFilterMenu: 'Ocultar Menu de Filtro',
    filterOperator: 'Operador de Filtro',
    filterConstraint: 'Restrição de Filtro',
    editRow: 'Editar Linha',
    saveEdit: 'Salvar Edição',
    cancelEdit: 'Cancelar Edição',
    listView: 'Visualização em Lista',
    gridView: 'Visualização em Grade',
    slide: 'Slide',
    slideNumber: '{slideNumber}',
    zoomImage: 'Ampliar Imagem',
    zoomIn: 'Ampliar',
    zoomOut: 'Reduzir',
    rotateRight: 'Girar para Direita',
    rotateLeft: 'Girar para Esquerda',
    listLabel: 'Lista de Opções',
    selectColor: 'Selecionar uma cor',
    removeLabel: 'Remover',
    browseFiles: 'Navegar Arquivos',
    maximizeLabel: 'Maximizar',
    minimizeLabel: 'Minimizar'
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      })
    ),
    provideHttpClient(withInterceptors([authInterceptor]), withJsonpSupport()),
    provideAnimations(),
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    provideEnvironmentNgxMask(),
    provideAppInitializer(initializeAuth),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideAppInitializer(() => {
      inject(PwaUpdateService).init();
    }),
    DialogService,
    ConfirmationService,
    MessageService,
    provideEnvironmentNgxCurrency({
      align: 'right',
      allowNegative: true,
      allowZero: true,
      decimal: ',',
      precision: 2,
      prefix: 'R$ ',
      suffix: '',
      thousands: '.',
      nullable: true,
      min: null,
      max: null,
      inputMode: NgxCurrencyInputMode.Financial,
    }),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: Lara,
        options: {
          prefix: 'p',
          darkModeSelector: 'none',
          cssLayer: false
        }
      },
      zIndex: {
        modal: 1100,
        overlay: 1000,
        menu: 1000,
        tooltip: 1100
      },
      filterMatchModeOptions: {
        text: [FilterMatchMode.STARTS_WITH, FilterMatchMode.CONTAINS, FilterMatchMode.NOT_CONTAINS, FilterMatchMode.ENDS_WITH, FilterMatchMode.EQUALS, FilterMatchMode.NOT_EQUALS],
        numeric: [FilterMatchMode.EQUALS, FilterMatchMode.NOT_EQUALS, FilterMatchMode.LESS_THAN, FilterMatchMode.LESS_THAN_OR_EQUAL_TO, FilterMatchMode.GREATER_THAN, FilterMatchMode.GREATER_THAN_OR_EQUAL_TO],
        date: [FilterMatchMode.DATE_IS, FilterMatchMode.DATE_IS_NOT, FilterMatchMode.DATE_BEFORE, FilterMatchMode.DATE_AFTER]
      },
      translation: ptBrTranslations
    }),
  ],
};
